import { getLogger } from '../../diagnostics/logger';
import { call, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  CHANGE_SIMS_SELECTED,
  DESELECT_SIMS,
  ENSURE_DIRECTLY_AVAILABLE_COMMAND_TYPES,
  INITIAL_AUTHENTICATION,
  LOAD_DIRECTLY_AVAILABLE_COMMAND_TYPES
} from '../../actions/actionTypes';
import { LoadDirectlyAvailableCommandTypesFilter } from '../../actions/directlyAvailableCommandTypeActions';
import { Action } from '../../actions/commonActions';
import {
  loadDirectlyAvailableCommandTypes,
  loadDirectlyAvailableCommandTypesFailure,
  loadDirectlyAvailableCommandTypesPending,
  loadDirectlyAvailableCommandTypesSuccess
} from '../../actions/directlyAvailableCommandTypeActions';
import * as commandTypeApi from '../../api/commandTypeApi';
import { HashMap } from '../../constants';
import { GridSelection } from '../../models';
import { AccountDetail } from '../../models/account';
import { getSelectedStockSerialNumbers, Sim, SimInfo } from '../../models/sim';
import { DirectlyAvailableSimCommandTypesState } from '../../models/state/directlyAvailableCommandTypeState';
import { selectDirectlyAvailableCommandTypes } from '../../selectors/directlyAvailableCommandTypes';
import { selectAccount } from '../../selectors/filter';
import { selectSelectedSims } from '../../selectors/sims';

const log = getLogger('simManagement.sagas.login');

/**
 * Ensure that the directly-available commands have been loaded.
 * @param action The action that invoked the saga.
 */
export function* ensureDirectlyAvailableCommandTypesSaga(action: Action<LoadDirectlyAvailableCommandTypesFilter>) {
  const payload = action.payload;
  if (typeof payload === 'undefined') {
    log.error('ensureDirectlyAvailableCommandsSaga payload is undefined');
    return;
  }

  const availableCommands: DirectlyAvailableSimCommandTypesState = yield select(selectDirectlyAvailableCommandTypes);
  if (!availableCommands.isLoading && (!availableCommands.expiresOn || availableCommands.expiresOn < new Date())) {
    yield put(loadDirectlyAvailableCommandTypes(payload));
  }
}

/**
 * Load the directly-available commands
 * @param action The action that invoked the saga.
 */
export function* loadDirectlyAvailableCommandTypesSaga(action: Action<LoadDirectlyAvailableCommandTypesFilter>) {
  if (typeof action.payload !== 'undefined') {
    yield put(loadDirectlyAvailableCommandTypesPending(action.payload));
    try {
      let commandTypeIds: number[] = [];
      if (action.payload.serialNumbers && action.payload.serialNumbers.length > 0) {
        commandTypeIds = yield call(commandTypeApi.getDirectlyAvailable, action.payload);
      }
      yield put(loadDirectlyAvailableCommandTypesSuccess(action.payload, commandTypeIds));
    } catch (error: any) {
      yield put(loadDirectlyAvailableCommandTypesFailure(action.payload, error));
    }
  }
}

/**
 * Loads the available command types when SIMs are selected or deselected.
 * @param action The action that invoked the saga.
 */
export function* selectedSimsChangedSaga(action: Action<GridSelection<SimInfo>[] | Sim[]>) {
  const selectedSims: HashMap<Sim> = yield select(selectSelectedSims);
  const account: AccountDetail | undefined = yield select(selectAccount);
  const selectedSerialNumbers = getSelectedStockSerialNumbers(selectedSims, true);
  yield put(
    loadDirectlyAvailableCommandTypes({
      serialNumbers: selectedSerialNumbers,
      isAtlasAccount: !!(account && account.isAtlasAccount)
    })
  );
}

/*
 * Initialize all sagas related to directly-available command types.
 */
export function* initDirectlyAvailableCommandTypesSagas() {
  yield take(INITIAL_AUTHENTICATION);
  yield takeEvery(CHANGE_SIMS_SELECTED, selectedSimsChangedSaga);
  yield takeEvery(DESELECT_SIMS, selectedSimsChangedSaga);
  yield takeEvery(ENSURE_DIRECTLY_AVAILABLE_COMMAND_TYPES, ensureDirectlyAvailableCommandTypesSaga);
  yield takeEvery(LOAD_DIRECTLY_AVAILABLE_COMMAND_TYPES, loadDirectlyAvailableCommandTypesSaga);
}
